import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { apiErrorText, useRemindPayRequestMutation, type PayRequestView } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import {
  formatDate,
  formatDay,
  formatRub,
  groupByDepartment,
  invoiceLabel,
  type DepartmentGroup,
} from '../model';
import { daysAgo, remindersCount, requestsCount } from './text';
import { ErrorText, GroupHead, GroupMeta, GroupTitle, Notice, SectionCard, Small } from './styled';

/** На узком экране таблица прокручивается вбок, а не сжимает колонки в столбик. */
const Scroll = styled.div`
  overflow-x: auto;

  & td,
  & th {
    vertical-align: top;
  }
`;

const Person = styled.div`
  font-weight: 700;
  min-width: 160px;
`;

const Supplier = styled(Link)`
  font-weight: 600;
  color: inherit;
  overflow-wrap: anywhere;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Num = styled.div`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/** Давно ждём — выделяем: больше 20 дней с оплаты. */
const Days = styled(Small)<{ $late: boolean }>`
  color: ${({ theme, $late }) => ($late ? theme.colors.status.danger : theme.colors.text.secondary)};
  font-weight: ${({ $late }) => ($late ? 600 : 400)};
`;

const Done = styled(Small)`
  color: ${({ theme }) => theme.colors.status.success};
`;

const LATE_DAYS = 20;

interface Props {
  items: PayRequestView[];
  /** id отделов в порядке справочника «Службы». */
  order: string[];
}

/**
 * «Нет закрывающих» — с кого спросить документы. Оплаченные заявки без УПД,
 * акта или счёта-фактуры по отделам и сотрудникам; первым — тот, от кого ждём
 * дольше всех. Напоминания сервер шлёт сам; «Напомнить» — внеочередное.
 */
export function ClosingDebts({ items, order }: Props) {
  const groups = useMemo(() => groupByDepartment(items, order, 'debt'), [items, order]);
  const [remind] = useRemindPayRequestMutation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, { ok: boolean; text: string }>>({});

  const people = new Set(items.map((r) => r.created_by ?? r.initiator_name)).size;
  const paid = items.reduce((s, r) => s + r.paid_total, 0);

  async function onRemind(r: PayRequestView) {
    setBusyId(r.id);
    try {
      await remind(r.id).unwrap();
      setNotes((n) => ({ ...n, [r.id]: { ok: true, text: 'Напомнили' } }));
    } catch (err) {
      setNotes((n) => ({ ...n, [r.id]: { ok: false, text: apiErrorText(err, 'Не напомнили.') } }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <Notice $tone="info">
        Ждём закрывающие документы по {items.length}{' '}
        {pluralizeRu(items.length, [
          'оплаченной заявке',
          'оплаченным заявкам',
          'оплаченным заявкам',
        ])}{' '}
        на {formatRub(paid)} от {people}{' '}
        {pluralizeRu(people, ['сотрудника', 'сотрудников', 'сотрудников'])}. Напоминания ОСНОВА шлёт
        сама: через 5 рабочих дней после оплаты, потом раз в неделю. «Напомнить» — отправить
        напоминание сейчас.
      </Notice>
      {groups.map((g) => (
        <DepartmentDebts
          key={g.key}
          group={g}
          busyId={busyId}
          notes={notes}
          onRemind={(r) => void onRemind(r)}
        />
      ))}
    </>
  );
}

interface DepartmentProps {
  group: DepartmentGroup;
  busyId: string | null;
  notes: Record<string, { ok: boolean; text: string }>;
  onRemind: (r: PayRequestView) => void;
}

function DepartmentDebts({ group, busyId, notes, onRemind }: DepartmentProps) {
  return (
    <SectionCard as="section" aria-label={group.name}>
      <GroupHead>
        <GroupTitle>{group.name}</GroupTitle>
        <GroupMeta>
          {requestsCount(group.count)} · оплачено {formatRub(group.paid)}
        </GroupMeta>
      </GroupHead>
      <Scroll>
        <Table>
          <thead>
            <tr>
              <th>Сотрудник</th>
              <th>Поставщик</th>
              <th>Сумма</th>
              <th>№ ФП</th>
              <th>Оплачено</th>
              <th>Напоминания</th>
              <th aria-label="Действие" />
            </tr>
          </thead>
          <tbody>
            {group.people.map((p) =>
              p.items.map((r, i) => {
                const note = notes[r.id];
                return (
                  <tr key={r.id}>
                    {i === 0 && (
                      <td rowSpan={p.items.length}>
                        <Person>{p.name}</Person>
                        <Small>без закрывающих: {p.items.length}</Small>
                      </td>
                    )}
                    <td>
                      <Supplier to={`/pay-requests/${r.id}`}>{r.counterparty_name}</Supplier>
                      {invoiceLabel(r) && <Small>{invoiceLabel(r)}</Small>}
                    </td>
                    <td>
                      <Num>{formatRub(r.amount)}</Num>
                      {r.paid_total < r.amount - 0.005 && (
                        <Small>оплачено {formatRub(r.paid_total)}</Small>
                      )}
                    </td>
                    <td>
                      <Num>{r.request_number ?? '—'}</Num>
                    </td>
                    <td>
                      <Num>{formatDate(r.first_paid_date)}</Num>
                      <Days $late={(r.days_since_paid ?? 0) > LATE_DAYS}>
                        {daysAgo(r.days_since_paid)}
                      </Days>
                    </td>
                    <td>
                      <div>{remindersCount(r.closing_reminders_sent)}</div>
                      <Small>
                        {r.closing_last_reminded_at &&
                          `последнее — ${formatDay(r.closing_last_reminded_at)}`}
                        {r.closing_last_reminded_at && r.next_reminder_date && ', '}
                        {r.next_reminder_date && `следующее — ${formatDate(r.next_reminder_date)}`}
                      </Small>
                    </td>
                    <td>
                      {r.can_manage_closing && (
                        <Button
                          type="button"
                          $variant="secondary"
                          $size="sm"
                          onClick={() => onRemind(r)}
                          disabled={busyId === r.id}
                          aria-label={`Напомнить ${p.name} о закрывающих по ${r.request_number ?? 'заявке'}`}
                        >
                          {busyId === r.id ? 'Напоминаем…' : 'Напомнить'}
                        </Button>
                      )}
                      {note &&
                        (note.ok ? (
                          <Done role="status">{note.text}</Done>
                        ) : (
                          <ErrorText role="alert">{note.text}</ErrorText>
                        ))}
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </Table>
      </Scroll>
    </SectionCard>
  );
}
